import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "../App";
import { useSubscription } from "../components/SubscriptionGate";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Check, Crown, Sparkles, Shield, Zap, Star,
  Brain, BarChart3, Gamepad2, Gem, Moon, FileText
} from "lucide-react";
import { toast } from "sonner";
import { devError } from "../utils/devLogger";
import { trackMetaEvent } from "../utils/metaTracking";

const PRO_FEATURES = [
  "Unlimited AI training plans",
  "3 AI meditations per day",
  "All 5 brain games, all difficulties",
  "Workout Lab & custom exercises",
  "Weekly AI reports & digest",
  "Leaderboard ranking",
  "Shareable profile card",
  "Priority support",
];

// Diamond is positioned as "Clinical-grade resilience training",
// not "Pro + sleep stories". Copy leads with the Deep Dive clinical
// assessments and stress-recovery science — the unique wedge that
// justifies the price gap and drives higher-margin conversions.
const DIAMOND_FEATURES = [
  "Deep Dive clinical assessments (PHQ-9, GAD-7, PSS, PSQI)",
  "Personalised resilience score, tracked over time",
  "AI Sleep Stories — restorative narration, nightly",
  "Advanced mood & stress-recovery insights",
  "Studio-grade meditation voices (tts-1-hd)",
  "Everything in Pro, unlocked",
  "Founding member badge + lifetime price lock",
];

const Subscription = () => {
  const { t } = useTranslation();
  const { tier: currentTier, isDiamond: currentlyDiamond } = useSubscription();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [billingCycle, setBillingCycle] = useState("yearly");
  const [founding, setFounding] = useState(null); // { cap, taken, remaining, closed }

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, statusRes, foundingRes] = await Promise.all([
        axios.get(`${API}/subscription/plans`),
        axios.get(`${API}/subscription/status`),
        axios.get(`${API}/subscription/founding-member-status`),
      ]);
      setPlans(plansRes.data.plans);
      setCurrentSub(statusRes.data);
      setFounding(foundingRes.data);
    } catch (err) {
      devError("Failed to load subscription data", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCheckout = async (planId) => {
    setCheckoutLoading(planId);
    const plan = plans.find(p => p.id === planId);
    trackMetaEvent(
      "InitiateCheckout",
      { content_ids: [planId], content_type: "product", value: plan?.amount, currency: "USD" },
    );
    try {
      const res = await axios.post(`${API}/subscription/checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error("Failed to initiate checkout");
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleStartTrial = async (planId) => {
    // Kept for backward compat with any UI still referencing this handler.
    // Trials are unsupported by the emergent payment library; delegate to
    // the regular checkout so users are never charged silently.
    return handleCheckout(planId);
  };

  const isActive = currentSub?.status === "active";
  const currentPlanId = currentSub?.plan;

  // Get plans for the selected billing cycle
  const proPlan = plans.find(p => p.id === (billingCycle === "yearly" ? "yearly" : "monthly"));
  const diamondPlan = plans.find(p => p.id === (billingCycle === "yearly" ? "diamond_yearly" : "diamond_monthly"));

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="subscription-page">
      <div className="fixed top-0 left-1/3 w-80 h-80 bg-sky-300/15 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 right-1/4 w-96 h-96 bg-violet-300/10 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-sky-600 hover:text-sky-800"
            data-testid="subscription-back-btn"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            {t("common.dashboard")}
          </Button>
          {isActive && (
            <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${
              currentlyDiamond ? "bg-violet-50 text-violet-600" : "bg-emerald-50 text-emerald-600"
            }`}>
              {currentlyDiamond ? <Gem className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Shield className="w-3.5 h-3.5" strokeWidth={1.5} />}
              {currentlyDiamond ? "Diamond Active" : "Pro Active"}
            </span>
          )}
        </div>
      </header>

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 text-sky-600 text-xs font-semibold uppercase tracking-wider mb-4">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} />
              {t("subscription.badge")}
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-sky-900 mb-3" style={{ fontFamily: "Outfit" }}>
              {t("subscription.title")}
            </h1>
            <p className="text-slate-500 max-w-lg mx-auto text-sm sm:text-base leading-relaxed">
              {t("subscription.description")}
            </p>
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center bg-white rounded-full p-1 shadow-sm border border-sky-100" data-testid="billing-toggle">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  billingCycle === "monthly" ? "bg-sky-500 text-white shadow-md" : "text-slate-500 hover:text-sky-700"
                }`}
                data-testid="billing-monthly-btn"
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle("yearly")}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                  billingCycle === "yearly" ? "bg-sky-500 text-white shadow-md" : "text-slate-500 hover:text-sky-700"
                }`}
                data-testid="billing-yearly-btn"
              >
                Yearly
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                  billingCycle === "yearly" ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-600"
                }`}>
                  Save 26%+
                </span>
              </button>
            </div>
          </div>

          {/* Current Status */}
          {isActive && (
            <div className={`glass-card p-6 mb-8 border ${currentlyDiamond ? "border-violet-200 bg-violet-50/30" : "border-emerald-200 bg-emerald-50/30"}`} data-testid="subscription-active-banner">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${currentlyDiamond ? "bg-violet-100" : "bg-emerald-100"}`}>
                  {currentlyDiamond ? <Gem className="w-6 h-6 text-violet-600" strokeWidth={1.5} /> : <Shield className="w-6 h-6 text-emerald-600" strokeWidth={1.5} />}
                </div>
                <div>
                  <p className={`font-medium ${currentlyDiamond ? "text-violet-800" : "text-emerald-800"}`}>
                    {currentlyDiamond ? "Diamond" : "Pro"} Active — {(currentPlanId || "").includes("year") ? t("subscription.yearly") : t("subscription.monthly")}
                  </p>
                  <p className={`text-xs ${currentlyDiamond ? "text-violet-600" : "text-emerald-600"}`}>
                    {t("subscription.expiresAt")}: {currentSub.expires_at ? new Date(currentSub.expires_at).toLocaleDateString() : "—"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Plan Cards */}
          <div className="grid gap-6 md:grid-cols-2 mb-12">
            {/* Pro Plan */}
            {proPlan && (() => {
              const isCurrentPlan = currentPlanId === proPlan.id && isActive;
              return (
                <div
                  className="relative glass-card p-8 border border-sky-100 transition-all duration-300 hover:shadow-lg"
                  data-testid={`plan-card-${proPlan.id}`}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="p-2.5 rounded-xl bg-amber-50">
                      <Crown className="w-6 h-6 text-amber-500" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-sky-900" style={{ fontFamily: "Outfit" }}>Pro</h3>
                      <p className="text-xs text-slate-400">{proPlan.description}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <span className="text-4xl font-light text-sky-900" style={{ fontFamily: "Outfit" }}>
                      ${proPlan.amount}
                    </span>
                    <span className="text-sm text-slate-400 ml-1">
                      / {proPlan.interval === "year" ? t("subscription.year") : t("subscription.month")}
                    </span>
                    {billingCycle === "yearly" && (
                      <p className="text-xs text-emerald-500 mt-1 font-medium">
                        ${(proPlan.amount / 12).toFixed(2)}/{t("subscription.month")} — {t("subscription.save33")}
                      </p>
                    )}
                  </div>

                  <ul className="space-y-3 mb-8">
                    {PRO_FEATURES.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-sky-400 shrink-0 mt-0.5" strokeWidth={2} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button
                    onClick={() => handleCheckout(proPlan.id)}
                    disabled={checkoutLoading === proPlan.id || isCurrentPlan}
                    className="w-full bg-slate-100 text-sky-900 hover:bg-slate-200"
                    data-testid={`checkout-btn-${proPlan.id}`}
                  >
                    {checkoutLoading === proPlan.id ? (
                      <div className="w-5 h-5 rounded-full border-2 border-sky-400/30 border-t-sky-400 animate-spin" />
                    ) : isCurrentPlan ? (
                      <>{t("subscription.currentPlan")}</>
                    ) : (
                      <>{t("subscription.subscribe")}</>
                    )}
                  </Button>
                </div>
              );
            })()}

            {/* Diamond Plan — positioned as "Clinical-grade resilience training" */}
            {diamondPlan && (() => {
              const isCurrentPlan = currentPlanId === diamondPlan.id && isActive;
              return (
                <div
                  className="relative glass-card p-8 border-2 border-violet-400 shadow-[0_0_30px_-5px_rgba(139,92,246,0.3)] transition-all duration-300"
                  data-testid={`plan-card-${diamondPlan.id}`}
                >
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-violet-500 to-indigo-500 text-white text-xs font-semibold uppercase tracking-wider">
                    Clinical Tier · Best Value
                  </div>

                  {/* Founding Member scarcity counter — real-data-backed */}
                  {founding && (() => {
                    const pctTaken = Math.min(100, Math.round((founding.taken / founding.cap) * 100));
                    const isCritical = founding.remaining <= 10 && founding.remaining > 0;
                    const isClosed = founding.closed;
                    return (
                      <div className="mb-5 pt-2" data-testid="founding-member-counter">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <span className={`w-2 h-2 rounded-full ${isClosed ? "bg-slate-400" : isCritical ? "bg-amber-500 animate-pulse" : "bg-emerald-500 animate-pulse"}`} />
                            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-700">
                              {isClosed ? "Founding Round Closed" : isCritical ? "Almost Full" : "Founding Round Open"}
                            </span>
                          </div>
                          <span className="text-xs font-medium text-violet-700" data-testid="founding-member-remaining">
                            {isClosed ? "0" : founding.remaining} / {founding.cap} free spots left
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-violet-100 overflow-hidden">
                          <div
                            className={`h-full transition-all duration-1000 ${isClosed ? "bg-slate-400" : isCritical ? "bg-gradient-to-r from-amber-400 to-orange-500" : "bg-gradient-to-r from-violet-500 to-indigo-500"}`}
                            style={{ width: `${pctTaken}%` }}
                            data-testid="founding-member-progress"
                          />
                        </div>
                        {isCritical && (
                          <p className="text-[11px] text-amber-700 mt-1.5 font-medium">
                            Only {founding.remaining} lifetime-price-lock spot{founding.remaining === 1 ? "" : "s"} remaining.
                          </p>
                        )}
                        {isClosed && (
                          <p className="text-[11px] text-slate-500 mt-1.5">
                            All 50 founding spots taken. Diamond is still available — pricing reflects the new tier.
                          </p>
                        )}
                      </div>
                    );
                  })()}

                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2.5 rounded-xl bg-violet-50">
                      <Gem className="w-6 h-6 text-violet-500" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-sky-900" style={{ fontFamily: "Outfit" }}>Diamond</h3>
                      <p className="text-xs text-violet-600 font-medium uppercase tracking-wide">Clinical-grade resilience</p>
                    </div>
                  </div>

                  <p className="text-sm text-slate-600 leading-relaxed mb-5" data-testid="diamond-positioning">
                    Built with validated clinical instruments (PHQ-9, GAD-7, PSS, PSQI). Track your resilience score over time and get AI insights normally reserved for a therapist&apos;s intake session.
                  </p>

                  <div className="mb-6">
                    <span className="text-4xl font-light text-sky-900" style={{ fontFamily: "Outfit" }}>
                      ${diamondPlan.amount}
                    </span>
                    <span className="text-sm text-slate-400 ml-1">
                      / {diamondPlan.interval === "year" ? t("subscription.year") : t("subscription.month")}
                    </span>
                    {billingCycle === "yearly" && (
                      <p className="text-xs text-emerald-500 mt-1 font-medium">
                        ${(diamondPlan.amount / 12).toFixed(2)}/{t("subscription.month")} — Save over 30%
                      </p>
                    )}
                    <p className="text-[11px] text-slate-400 mt-1">
                      Less than a single therapy co-pay per month.
                    </p>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {DIAMOND_FEATURES.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-sm text-slate-600">
                        <Gem className="w-4 h-4 text-violet-400 shrink-0 mt-0.5" strokeWidth={2} />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="mb-6 p-3 rounded-xl bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-100">
                    <p className="text-[11px] uppercase tracking-wider text-violet-700 font-semibold mb-1">Founding Member</p>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Your price is locked in for life. Even when we raise prices for new users, yours stays where it is today.
                    </p>
                  </div>

                  <Button
                    onClick={() => handleCheckout(diamondPlan.id)}
                    disabled={checkoutLoading === diamondPlan.id || isCurrentPlan || (founding && founding.closed)}
                    className="w-full bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 text-white shadow-lg disabled:opacity-60"
                    data-testid={`checkout-btn-${diamondPlan.id}`}
                  >
                    {checkoutLoading === diamondPlan.id ? (
                      <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    ) : isCurrentPlan ? (
                      <>{t("subscription.currentPlan")}</>
                    ) : founding && founding.closed ? (
                      <>Founding Round Closed</>
                    ) : (
                      <><Gem className="w-4 h-4 mr-1.5" /> Unlock Clinical Tier</>
                    )}
                  </Button>
                </div>
              );
            })()}
          </div>

          {/* Feature Comparison */}
          <div className="glass-card p-8">
            <h3 className="text-lg font-medium text-sky-800 mb-6 text-center" style={{ fontFamily: "Outfit" }}>
              {t("subscription.whatsIncluded")}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Brain, label: t("subscription.featureAI"), desc: t("subscription.featureAIDesc") },
                { icon: Gamepad2, label: t("subscription.featureGames"), desc: t("subscription.featureGamesDesc") },
                { icon: Moon, label: "Sleep Stories", desc: "Diamond-tier AI bedtime stories" },
                { icon: FileText, label: "Deep Dive", desc: "Diamond-tier clinical assessments" },
              ].map((f) => (
                <div key={f.label} className="text-center p-4">
                  <f.icon className="w-8 h-8 text-sky-400 mx-auto mb-2" strokeWidth={1.5} />
                  <p className="text-sm font-medium text-sky-800 mb-1">{f.label}</p>
                  <p className="text-xs text-slate-400">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Subscription;
